import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import classNames from 'classnames';

import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';

import AssetImage from './AssetImage';
import AssetDate from './AssetDate';
import AssetOptionsModal from '../modals/AssetOptionsModal';
import RenameAssetModal from '../modals/RenameAssetModal';
import MoveAssetModal from '../modals/MoveAssetModal';
import * as assetsConstants from '../../constants/assets';
import * as bucketSelectors from '../../selectors/bucketSelectors';
import * as assetSelectors from '../../selectors/assetSelectors';
import AssetPublishInfo from './AssetPublishInfo';
import DeleteAssetModal from '../modals/DeleteAssetModal';
import AssetPublishIndicator from './AssetPublishIndicator';
import AssetTags from './AssetTags';
import AssetTagsModal from '../modals/AssetTagsModal';
import { ASSET_TYPE_NAMES } from '../../enums/AssetType';

class Asset extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            showOptionsModal: false,
            showRenameModal: false,
            showMoveModal: false,
            showCopyModal: false,
            showDeleteModal: false,
            showTagModal: false,
        };
    }

    onMoveAsset = (asset, toBucket, isCopy) => {
        this.setState({
            showMoveModal: false,
            showCopyModal: false,
            showTagModal: false,
        });
        this.props.onMoveAsset(asset, toBucket, isCopy);
    };

    onShowTags = () => {
        this.setState({ showTagModal: true });
    }

    onSelect = (e) => {
        e.preventDefault();
        
        const { onSelect, asset, actions } = this.props;
        
        if (onSelect && actions && actions.length > 0) {
            onSelect(asset, e.shiftKey);
        }
    };

    onOpenPreview = () => {
        window.open(this.props.asset.previewURL, '_blank');
    };

    render() {
        const {
            asset,
            classes,
            isAdmin,
            onUpdateAssetName,
            onDeleteAsset,
            onUpdateAssetTags,
            isSelected,
            actions,
            anySelected,
            selectedApp,
        } = this.props;
        const {
            showOptionsModal,
            showRenameModal,
            showMoveModal,
            showCopyModal,
            showDeleteModal,
            showTagModal,
        } = this.state;
        const {
            previewURL,
            embedURL,
            hasChangesAfterLastPublish,
            visualizationType,
            title,
            updatedAt,
            tags,
            context,
        } = asset;

        const wrapperClasses = classNames(classes.wrapper, {
            [classes.selected]: isSelected,
            [classes.selectDisabled]: !actions || actions.length === 0,
        });

        const shouldShowVisualizationType = selectedApp === 'all';

        return (
            <Paper square elevation={1} className={wrapperClasses} data-testid="asset-item">
                <div className={classes.selectArea}>
                    <div className={classes.imageContainer} onClick={this.onSelect}>
                        <AssetImage
                            isSelected={isSelected}
                            visualizationType={visualizationType}
                            anySelected={anySelected}
                        />
                    </div>
                    <div className={classes.info} onClick={this.onOpenPreview}>
                        <div className={classes.title}>
                            <Tooltip title={title}>
                                <Typography className={classes.titleText}>{title}</Typography>
                            </Tooltip>
                            <AssetPublishIndicator
                                hasChangesAfterLastPublish={
                                    hasChangesAfterLastPublish
                                }
                            />
                        </div>
                        <div className={classes.detailedInfo}>
                            {shouldShowVisualizationType && context && (
                                <React.Fragment>
                                    <Typography variant="caption" className={classes.visualizationType}>
                                        {ASSET_TYPE_NAMES[context]}
                                    </Typography>
                                    <div className={classes.oval} />
                                </React.Fragment>
                            )}
                            <AssetDate date={updatedAt} />
                            {tags && tags.length > 0 && (
                                <React.Fragment>
                                    <div className={classes.oval} />
                                    <AssetTags assetTags={tags || []} />
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                </div>

                {!anySelected && (
                    <div className={classes.actionWrapper}>
                        <AssetPublishInfo
                            embedURL={embedURL}
                        hasChangesAfterLastPublish={hasChangesAfterLastPublish}
                    />

                    <button
                        aria-label="Tag"
                        className={classes.tag}
                        onClick={this.onShowTags}
                    >
                        <i className="material-icons">local_offer</i>
                    </button>

                    <button
                        aria-label="Additional options"
                        className={classes.assetOptions}
                            onClick={() => this.setState({ showOptionsModal: true })}
                        />
                    </div>
                )}

                {showTagModal && <AssetTagsModal
                    assetTags={tags || []}
                    onClose={() => this.setState({ showTagModal: false })}
                    onUpdateTags={(assetTags) => onUpdateAssetTags(asset, assetTags)}
                />}

                <RenameAssetModal
                    show={showRenameModal}
                    asset={asset}
                    onUpdate={onUpdateAssetName}
                    onClose={() => this.setState({ showRenameModal: false })}
                />

                <MoveAssetModal
                    show={showMoveModal || showCopyModal}
                    isAdmin={isAdmin}
                    asset={asset}
                    copyAsset={showCopyModal}
                    onMoveTo={this.onMoveAsset}
                    onClose={() =>
                        this.setState({
                            showMoveModal: false,
                            showCopyModal: false,
                        })
                    }
                />

                <DeleteAssetModal
                    asset={asset}
                    show={showDeleteModal}
                    onDelete={onDeleteAsset}
                    onClose={() => this.setState({ showDeleteModal: false })}
                />

                <AssetOptionsModal
                    asset={asset}
                    isAdmin={isAdmin}
                    show={showOptionsModal}
                    onEdit={() => window.open(previewURL, '_blank')}
                    onViewPublished={
                        embedURL ? () => window.open(embedURL, '_blank') : null
                    }
                    onRename={() =>
                        this.setState({
                            showRenameModal: true,
                            showOptionsModal: false,
                        })
                    }
                    onMoveAsset={() =>
                        this.setState({
                            showMoveModal: true,
                            showOptionsModal: false,
                        })
                    }
                    onCopyAsset={() =>
                        this.setState({
                            showCopyModal: true,
                            showOptionsModal: false,
                        })
                    }
                    onDeleteAsset={() =>
                        this.setState({
                            showDeleteModal: true,
                            showOptionsModal: false,
                        })
                    }
                    onClose={() => this.setState({ showOptionsModal: false })}
                />
            </Paper>
        );
    }
}

Asset.propTypes = {
    asset: PropTypes.object.isRequired,
    onUpdateAssetName: PropTypes.func.isRequired,
    onUpdateAssetTags: PropTypes.func.isRequired,
    onSelect: PropTypes.func,
    isSelected: PropTypes.bool,
    actions: PropTypes.array,
    anySelected: PropTypes.bool,
};

Asset.defaultProps = {
    onSelect: undefined,
    isSelected: false,
    actions: [],
    anySelected: false,
};

const style = ({ spacing, palette }) => ({
    wrapper: {
        display: 'flex',
        alignItems: 'center',
        padding: [0, spacing.unit * 3],
        '&:hover': { background: palette.background.gray1Color },
    },
    actionWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    selected: {
        background: palette.background.gray1Color,
    },
    selectArea: {
        display: 'flex',
        alignItems: 'center',
        flexGrow: 1,
        minWidth: 0,
        overflow: 'hidden',
    },
    imageContainer: {
        cursor: 'pointer',
        padding: spacing.unit,
    },
    selectDisabled: {
        '& $imageContainer': {
            cursor: 'default',
        },
    },
    info: {
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column',
        padding: [0, spacing.unit * 2],
        cursor: 'pointer',
        overflow: 'hidden',
    },
    detailedInfo: {
        display: 'flex',
        alignItems: 'center',
    },
    title: {
        display: 'flex',
        width: '100%',
        overflow: 'hidden',
        alignItems: 'center',
    },
    titleText: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        margin: 0,
        maxWidth: '95%',
    },
    tag: {
        display: 'flex',
        alignItems: 'center',
        outline: 'none',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        width: spacing.unit * 6,
        height: spacing.unit * 6,
        color: palette.action.active,
        '&:hover': {
            color: palette.text.secondary,
        },
    },
    assetOptions: {
        width: 32,
        height: 24,
        cursor: 'pointer',
        border: 'none',
        outline: 'none',
        background: `url(/assets/menu_icon.svg) no-repeat`,
        '&:hover': {
            background: `url(/assets/menu_icon_selected.svg) no-repeat`,
        },
    },
    oval: {
        borderRadius: '50%',
        backgroundColor: '#d8d8d8',
        height: '5px',
        width: '5px',
        marginLeft: '10px',
        marginRight: '10px',
    },
});

const mapStateToProps = state => ({
    isAdmin: bucketSelectors.getIsCurrentUserAdminForCurrentBucket(state),
    selectedApp: assetSelectors.getApplication(state),
});

const mapDispatchToProps = dispatch => ({
    onUpdateAssetName: (asset, title) => {
        dispatch({
            type: assetsConstants.UPDATE_ASSET_REQUEST,
            payload: {
                asset,
                updatedAsset: {
                    title,
                },
            },
        });
    },
    onMoveAsset: (asset, toBucket, isCopy) => {
        dispatch({
            type: assetsConstants.MOVE_ASSET_REQUEST,
            payload: {
                asset,
                toBucket,
                isCopy,
            },
        });
    },
    onDeleteAsset: asset => {
        dispatch({
            type: assetsConstants.DELETE_ASSET_REQUEST,
            payload: {
                asset,
            },
        });
    },
    onUpdateAssetTags: (asset, tags) => {
        dispatch({
            type: assetsConstants.UPDATE_ASSET_REQUEST,
            payload: {
                asset,
                updatedAsset: {
                    tags,
                },
            },
        });
    },
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(injectSheet(style)(Asset));



// WEBPACK FOOTER //
// ./src/components/assets/Asset.js